<div class="tenant-dialog-container">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-content">
      <h2 mat-dialog-title>{{ dialogTitle }}</h2>
      <p class="header-subtitle">
        @if (data.mode === 'create') {
          Create a new tenant organization
        } @else if (data.mode === 'edit') {
          Update tenant information
        } @else {
          View tenant details and assigned modules
        }
      </p>
    </div>
    <button mat-icon-button (click)="onCancel()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    @if (!isReadOnly) {
      <!-- Edit/Create Form -->
      <form [formGroup]="tenantForm" class="tenant-form edit-mode-form">
        <!-- Basic Information Section -->
        <div class="form-section">
          <div class="section-header">
            <mat-icon>business</mat-icon>
            <h3>Basic Information</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Tenant Name <span class="required">*</span></label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="name"
                       placeholder="Enter organization name">
                <mat-icon matPrefix>business</mat-icon>
                @if (tenantForm.get('name')?.hasError('required')) {
                  <mat-error>Tenant name is required</mat-error>
                }
                @if (tenantForm.get('name')?.hasError('serverError')) {
                  <mat-error>{{ tenantForm.get('name')?.getError('serverError') }}</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="form-group">
              <label class="form-label">Slug <span class="required">*</span></label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="slug"
                       placeholder="Enter unique identifier">
                <mat-icon matPrefix>tag</mat-icon>
                @if (tenantForm.get('slug')?.hasError('required')) {
                  <mat-error>Slug is required</mat-error>
                }
                @if (tenantForm.get('slug')?.hasError('serverError')) {
                  <mat-error>{{ tenantForm.get('slug')?.getError('serverError') }}</mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="form-label">Description</label>
              <mat-form-field appearance="outline" class="textarea-field">
                <textarea matInput
                          formControlName="description"
                          placeholder="Enter tenant description"
                          rows="3"></textarea>
                <mat-icon matPrefix>description</mat-icon>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Contact Information Section -->
        <div class="form-section">
          <div class="section-header">
            <mat-icon>contact_mail</mat-icon>
            <h3>Contact Information</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Contact Email <span class="required">*</span></label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="contactEmail"
                       placeholder="contact@example.com">
                <mat-icon matPrefix>email</mat-icon>
                @if (tenantForm.get('contactEmail')?.hasError('required')) {
                  <mat-error>Contact email is required</mat-error>
                }
                @if (tenantForm.get('contactEmail')?.hasError('email') && !tenantForm.get('contactEmail')?.hasError('required')) {
                  <mat-error>Please enter a valid email</mat-error>
                }
                @if (tenantForm.get('contactEmail')?.hasError('serverError')) {
                  <mat-error>{{ tenantForm.get('contactEmail')?.getError('serverError') }}</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="form-group">
              <label class="form-label">Contact Phone</label>
                <ngx-intl-tel-input
                    [cssClass]="'form-control'"
                    [preferredCountries]="preferredCountries"
                    [enableAutoCountrySelect]="true"
                    [enablePlaceholder]="true"
                    [searchCountryFlag]="true"
                    [searchCountryField]="[SearchCountryField.All]"
                    [selectFirstCountry]="false"
                    [selectedCountryISO]="selectedCountryISO"
                    [maxLength]="15"
                    [phoneValidation]="false"
                    [separateDialCode]="separateDialCode"
                    formControlName="phoneNumber"
                    (countryChange)="onPhoneNumberChange()">
                  </ngx-intl-tel-input>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="form-label">Website</label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="website"
                       placeholder="https://example.com">
                <mat-icon matPrefix>language</mat-icon>
                @if (tenantForm.get('website')?.hasError('invalidUrl')) {
                  <mat-error>Please enter a valid web address (e.g., https://example.com)</mat-error>
                }
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Location Information Section -->
        <div class="form-section">
          <div class="section-header">
            <mat-icon>location_on</mat-icon>
            <h3>Location Information</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">City</label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="city"
                       placeholder="Enter city">
                <mat-icon matPrefix>location_city</mat-icon>
              </mat-form-field>
            </div>

            <div class="form-group">
              <label class="form-label">Country</label>
              <mat-form-field appearance="outline">
                <input matInput
                       formControlName="country"
                       placeholder="Enter country">
                <mat-icon matPrefix>public</mat-icon>
              </mat-form-field>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="form-label">Address</label>
              <mat-form-field appearance="outline" class="textarea-field">
                <textarea matInput
                          formControlName="address"
                          placeholder="Enter full address"
                          rows="2"></textarea>
                <mat-icon matPrefix>home</mat-icon>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- User Limits Section -->
        <div class="form-section">
          <div class="section-header">
            <mat-icon>people</mat-icon>
            <h3>User Limits</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Allowed System Users <span class="required">*</span></label>
              <mat-checkbox formControlName="unlimitedSystemUser"
                           (change)="onUnlimitedSystemUserChange($event.checked)"
                           color="primary"
                           class="unlimited-checkbox">
                Unlimited
              </mat-checkbox>
              @if (!tenantForm.get('unlimitedSystemUser')?.value) {
                <mat-form-field appearance="outline">
                  <input matInput
                         type="number"
                         formControlName="allowedSystemUser"
                         placeholder="0"
                         min="0">
                  <mat-icon matPrefix>person</mat-icon>
                  @if (tenantForm.get('allowedSystemUser')?.hasError('required')) {
                    <mat-error>Allowed system users is required</mat-error>
                  }
                  @if (tenantForm.get('allowedSystemUser')?.hasError('min')) {
                    <mat-error>Value must be 0 or greater</mat-error>
                  }
                  @if (tenantForm.get('allowedSystemUser')?.hasError('serverError')) {
                    <mat-error>{{ tenantForm.get('allowedSystemUser')?.getError('serverError') }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>

            <div class="form-group">
              <label class="form-label">Allowed Contracted Users <span class="required">*</span></label>
              <mat-checkbox formControlName="unlimitedContractedUser"
                           (change)="onUnlimitedContractedUserChange($event.checked)"
                           color="primary"
                           class="unlimited-checkbox">
                Unlimited
              </mat-checkbox>
              @if (!tenantForm.get('unlimitedContractedUser')?.value) {
                <mat-form-field appearance="outline">
                  <input matInput
                         type="number"
                         formControlName="allowedContractedUser"
                         placeholder="0"
                         min="0">
                  <mat-icon matPrefix>person_outline</mat-icon>
                  @if (tenantForm.get('allowedContractedUser')?.hasError('required')) {
                    <mat-error>Allowed contracted users is required</mat-error>
                  }
                  @if (tenantForm.get('allowedContractedUser')?.hasError('min')) {
                    <mat-error>Value must be 0 or greater</mat-error>
                  }
                  @if (tenantForm.get('allowedContractedUser')?.hasError('serverError')) {
                    <mat-error>{{ tenantForm.get('allowedContractedUser')?.getError('serverError') }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </div>
        </div>

        <!-- Status Section -->
        <div class="form-section">
          <div class="section-header">
            <mat-icon>toggle_on</mat-icon>
            <h3>Status Settings</h3>
          </div>

          <div class="checkbox-field">
            <mat-checkbox formControlName="isActive" color="primary">
              <div class="checkbox-label">
                <span class="label-text">Active Status</span>
                <span class="label-hint">Enable this tenant for system access</span>
              </div>
            </mat-checkbox>
          </div>
        </div>
      </form>
    } @else {
      <!-- View Mode -->
      <div class="view-content">
        <!-- Tenant Overview Section -->
        <div class="view-section">
          <div class="section-header">
            <mat-icon>business</mat-icon>
            <h3>Tenant Overview</h3>
          </div>

          <div class="view-grid">
            <div class="view-item">
              <div class="view-label">
                <mat-icon>business</mat-icon>
                <span>Tenant Name</span>
              </div>
              <div class="view-value">{{ data.tenant?.name || 'N/A' }}</div>
            </div>

            <div class="view-item">
              <div class="view-label">
                <mat-icon>tag</mat-icon>
                <span>Slug</span>
              </div>
              <div class="view-value slug-value">{{ data.tenant?.slug || 'N/A' }}</div>
            </div>

            <div class="view-item full-width">
              <div class="view-label">
                <mat-icon>description</mat-icon>
                <span>Description</span>
              </div>
              <div class="view-value">{{ data.tenant?.description || 'No description provided' }}</div>
            </div>

            <div class="view-item">
              <div class="view-label">
                <mat-icon>toggle_on</mat-icon>
                <span>Status</span>
              </div>
              <div class="view-value">
                <span class="status-badge" [class.active]="data.tenant?.isActive" [class.inactive]="!data.tenant?.isActive">
                  <mat-icon>{{ data.tenant?.isActive ? 'check_circle' : 'cancel' }}</mat-icon>
                  {{ data.tenant?.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Information Section -->
        <div class="view-section">
          <div class="section-header">
            <mat-icon>contact_mail</mat-icon>
            <h3>Contact Information</h3>
          </div>

          <div class="view-grid">
            @if (data.tenant?.contactEmail) {
              <div class="view-item">
                <div class="view-label">
                  <mat-icon>email</mat-icon>
                  <span>Email</span>
                </div>
                <div class="view-value">{{ data.tenant?.contactEmail }}</div>
              </div>
            }

            @if (data.tenant?.contactPhone) {
              <div class="view-item">
                <div class="view-label">
                  <mat-icon>phone</mat-icon>
                  <span>Phone</span>
                </div>
                <div class="view-value">
                  @if (data.tenant) {
                    @if (data.tenant.contactPhoneCountryCode) {
                      {{ data.tenant.contactPhoneCountryCode }} {{ data.tenant.contactPhone }}
                    } @else if (data.tenant.contactPhone) {
                      {{ data.tenant.contactPhone }}
                    }
                  }
                </div>
              </div>
            }

            @if (data.tenant?.website) {
              <div class="view-item full-width">
                <div class="view-label">
                  <mat-icon>language</mat-icon>
                  <span>Website</span>
                </div>
                <div class="view-value">
                  <a [href]="data.tenant?.website" target="_blank" rel="noopener">{{ data.tenant?.website }}</a>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Location Information Section -->
        @if (data.tenant?.city || data.tenant?.country || data.tenant?.address) {
          <div class="view-section">
            <div class="section-header">
              <mat-icon>location_on</mat-icon>
              <h3>Location</h3>
            </div>

            <div class="view-grid">
              @if (data.tenant?.city) {
                <div class="view-item">
                  <div class="view-label">
                    <mat-icon>location_city</mat-icon>
                    <span>City</span>
                  </div>
                  <div class="view-value">{{ data.tenant?.city }}</div>
                </div>
              }

              @if (data.tenant?.country) {
                <div class="view-item">
                  <div class="view-label">
                    <mat-icon>public</mat-icon>
                    <span>Country</span>
                  </div>
                  <div class="view-value">{{ data.tenant?.country }}</div>
                </div>
              }

              @if (data.tenant?.address) {
                <div class="view-item full-width">
                  <div class="view-label">
                    <mat-icon>home</mat-icon>
                    <span>Address</span>
                  </div>
                  <div class="view-value">{{ data.tenant?.address }}</div>
                </div>
              }
            </div>
          </div>
        }

        <!-- User Limits Section -->
        <div class="view-section">
          <div class="section-header">
            <mat-icon>people</mat-icon>
            <h3>User Limits</h3>
          </div>

          <div class="view-grid">
            <div class="view-item">
              <div class="view-label">
                <mat-icon>person</mat-icon>
                <span>Allowed System Users</span>
              </div>
              <div class="view-value">{{ data.tenant?.allowedSystemUser === -1 ? 'Unlimited' : (data.tenant?.allowedSystemUser ?? 0) }}</div>
            </div>

            <div class="view-item">
              <div class="view-label">
                <mat-icon>person_outline</mat-icon>
                <span>Allowed Contracted Users</span>
              </div>
              <div class="view-value">{{ data.tenant?.allowedContractedUser === -1 ? 'Unlimited' : (data.tenant?.allowedContractedUser ?? 0) }}</div>
            </div>
          </div>
        </div>

        <!-- Assigned Modules Section -->
        <div class="view-section">
          <div class="section-header">
            <mat-icon>apps</mat-icon>
            <h3>Assigned Modules</h3>
            <button mat-button color="primary" (click)="openModuleAssignment()" class="header-action-btn">
              <mat-icon>settings</mat-icon>
              Manage
            </button>
          </div>

          @if (assignedModules.length > 0) {
            <div class="modules-grid">
              @for (tenantModule of assignedModules; track tenantModule.id) {
                <div class="module-card" [class.inactive]="!tenantModule.isActive">
                  <div class="module-icon">
                    <mat-icon>{{ tenantModule.module.icon || 'apps' }}</mat-icon>
                  </div>
                  <div class="module-info">
                    <h4>{{ tenantModule.module.name }}</h4>
                    <p>{{ tenantModule.module.description || 'No description available' }}</p>
                    <div class="module-meta">
                      <span class="status-badge" [class.active]="tenantModule.isActive" [class.inactive]="!tenantModule.isActive">
                        <mat-icon>{{ tenantModule.isActive ? 'check_circle' : 'pause_circle' }}</mat-icon>
                        {{ tenantModule.isActive ? 'Active' : 'Inactive' }}
                      </span>
                      @if (tenantModule.module.type) {
                        <span class="type-badge">{{ tenantModule.module.type }}</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="empty-modules">
              <mat-icon>apps</mat-icon>
              <p>No modules assigned to this tenant</p>
              <button mat-raised-button class="btn-primary" (click)="openModuleAssignment()">
                <mat-icon>add</mat-icon>
                <span>Assign Modules</span>
              </button>
            </div>
          }
        </div>
      </div>
    }
  </mat-dialog-content>

  <!-- Dialog Actions -->
  <mat-dialog-actions class="dialog-actions">
    <button mat-raised-button class="btn-secondary btn-with-icon" (click)="onCancel()">
      <mat-icon>close</mat-icon>
      <span>{{ isReadOnly ? 'Close' : 'Cancel' }}</span>
    </button>

    @if (!isReadOnly) {
      <button mat-raised-button
              class="btn-primary btn-with-icon"
              (click)="onSave()"
              [disabled]="tenantForm.invalid || isLoading">
        <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
        <mat-icon *ngIf="!isLoading">{{ data.mode === 'create' ? 'add' : 'save' }}</mat-icon>
        <span *ngIf="!isLoading">{{ data.mode === 'create' ? 'Create Tenant' : 'Update Tenant' }}</span>
      </button>
    }
  </mat-dialog-actions>
</div>
